import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import { setCurrentPage, setSelectedSessionInfo } from "../../redux/admin/sessionInfo.slice";
import { fetchSessionInfos, addSessionInfo, updateSessionInfo } from "../../redux/admin/sessionInfo.thunk";
import { fetchSessionMasters } from "../../redux/admin/sessionMaster.thunk";
import { fetchStudents } from "../../redux/admin/student.thunk";
import { fetchCourseInstructors } from "../../redux/admin/courseInstructor.thunk";
import { fetchBranches } from "../../redux/admin/branch.thunk";
import { fetchBooks, fetchBookChapters } from "../../redux/admin/book.thunk";
import { clearBookData } from "../../redux/admin/book.slice";
import { selectBooks } from "../../redux/admin/book.selector";

import toastr from "toastr";
import "toastr/build/toastr.min.css";

/* ═════════════════════════ HELPERS ═════════════════════════ */
const LIMIT = 10;
const SEG_COLORS = ["#1d4ed8", "#0e7490", "#6d28d9", "#047857", "#b45309", "#be185d"];

const uid = () => `l_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
const hhmm = (t) => (t ? String(t).slice(0, 5) : "");
const toMin = (t) => {
  if (!t) return null;
  const [h, m] = String(t).split(":");
  return Number(h) * 60 + Number(m);
};
const calcDur = (s, e) => {
  const a = toMin(s), b = toMin(e);
  return a != null && b != null && b > a ? b - a : 0;
};
// true when both times are set but the end is not after the start
const isBadWindow = (s, e) => Boolean(s && e && toMin(e) <= toMin(s));
const addMinutes = (t, mins) => {
  const base = toMin(t);
  if (base == null) return "";
  const total = Math.min(base + mins, 23 * 60 + 59);
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
};
const fmtDur = (m) => {
  if (!m) return "0 min";
  const h = Math.floor(m / 60), r = m % 60;
  return [h ? `${h} hr` : "", r ? `${r} min` : ""].filter(Boolean).join(" ");
};
const ymd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const normalizeList = (res) => (Array.isArray(res) ? res : res?.chapters || res?.data || res?.items || []);
const studentName = (s) => s?.name || s?.student_name || s?.full_name || "";
const chapterId = (c) => c.chapter_id ?? c.id;

const newLesson = (start = "", end = "") => ({
  key: uid(), id: null, book_id: "", chapter_id: "", title: "", notes: "", start_time: start, end_time: end,
});

const makeEmptyForm = () => ({
  session_master_id: "", instructor_id: "", session_date: ymd(new Date()), start_time: "", end_time: "",
  notes: "", attendance_editable: 1, lessons: [newLesson()], attendance_roster: [],
});

const buildChapterOptions = (list = []) => {
  const num = (c) => parseFloat(c.chapter_no) || 0;
  const sorter = (a, b) => num(a) - num(b) || String(a.chapter_no || "").localeCompare(String(b.chapter_no || ""));
  const mains = list.filter((c) => !c.parent_chapter_id).sort(sorter);
  const subs = list.filter((c) => c.parent_chapter_id);
  const out = [];
  mains.forEach((m) => {
    out.push({ ...m, isSub: false });
    subs.filter((s) => String(s.parent_chapter_id) === String(m.chapter_id)).sort(sorter)
      .forEach((s) => out.push({ ...s, isSub: true }));
  });
  const seen = new Set(out.map((c) => String(chapterId(c))));
  list.forEach((c) => !seen.has(String(chapterId(c))) && out.push({ ...c, isSub: Boolean(c.parent_chapter_id) }));
  return out;
};
const chapterLabel = (c) =>
  `${c.isSub ? "↳ " : "Ch. "}${c.chapter_no ? `${c.chapter_no} · ` : ""}${c.chapter_name || c.title || c.name || ""}`;

const flattenRoster = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  return [...(raw.active_students || []), ...(raw.inactive_students || [])];
};

const getLessons = (instance) => {
  const items = instance.lessons || instance.lesson_items || [];
  if (items.length) {
    return items.map((item) => ({ ...item, title: item.title || item.book_title || "Untitled segment" }));
  }
  if (instance.topic || instance.book_title)
    return [{
      title: instance.book_title || instance.topic,
      chapter_name: instance.chapter_name,
      start_time: instance.start_time,
      end_time: instance.end_time,
      duration_minutes: calcDur(instance.start_time, instance.end_time),
    }];
  return [];
};

// A saved title that is only "Book" or "Book – Chapter" was generated automatically.
// It must not be copied into the Topic box, or it would go stale when the chapter changes.
const isAutoTitle = (l) =>
  !l.title || Boolean(l.book_title && (l.title === l.book_title || l.title.startsWith(`${l.book_title} – `)));

/* ═════════════════════════ SMALL COMPONENTS ═════════════════════════ */
const Spinner = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ animation: "si-spin .7s linear infinite" }} aria-hidden="true">
    <circle cx="12" cy="12" r="10" stroke="#fff" strokeOpacity=".25" strokeWidth="2" />
    <path d="M12 2a10 10 0 0 1 10 10" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const Skeleton = ({ w = "100%", h = 14, r = 6, style }) => (
  <div className="si-skel" style={{ width: w, height: h, borderRadius: r, ...style }} />
);

const RosterRow = ({ item, sObj, onToggle, onRemove, disabled }) => {
  const present = item.attendance_status === "Present";
  const inactive = String(item.student_current_status || "active").toLowerCase() !== "active";
  const name = item.name || studentName(sObj) || `Student #${item.student_id}`;
  const grade = item.grade !== "" && item.grade != null ? item.grade : sObj?.grade || "N/A";
  const interactive = !disabled && !inactive;
  return (
    <div
      className={`si-roster-row${present ? " present" : ""}${inactive ? " locked" : ""}${item.is_external ? " external" : ""}${disabled ? " readonly" : ""}`}
      onClick={() => interactive && onToggle(item.student_id)}
      onKeyDown={(e) => interactive && (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onToggle(item.student_id))}
      tabIndex={interactive ? 0 : -1}
      role={interactive ? "switch" : undefined}
      aria-checked={interactive ? present : undefined}
      title={inactive ? "Inactive student. Past attendance can't be changed." : undefined}
    >
      <div className="si-roster-left">
        <div className={`si-avatar ${present ? "on" : ""}`}>{(name[0] || "?").toUpperCase()}</div>
        <div className="si-min0">
          <div className="si-roster-name">
            <span className="si-ellipsis">{name}</span>
            {item.is_external && <span className="si-tag blue">Makeup</span>}
            {inactive && <span className="si-tag amber">Inactive</span>}
          </div>
          <div className="si-sub">Grade {grade}</div>
        </div>
      </div>
      <div className="si-roster-right">
        <span className={`si-status ${present ? "present" : "absent"}`}>
          <i className={present ? "fas fa-check-circle" : "fas fa-circle-xmark"}></i>
          {present ? "Present" : "Absent"}
        </span>
        {item.is_newly_added && (
          <button
            type="button" className="si-icon-btn danger" title="Remove before saving" aria-label="Remove attendee"
            disabled={disabled}
            onClick={(e) => { e.stopPropagation(); onRemove(item.student_id); }}
          >
            <i className="fas fa-trash-alt"></i>
          </button>
        )}
      </div>
    </div>
  );
};

/* Searchable student filter: never renders the full list, only the top matches for what was typed */
const StudentFilter = ({ students, value, onChange }) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const wrapRef = useRef(null);

  const selected = value ? students.find((s) => String(s.id) === String(value)) : null;
  const q = query.trim().toLowerCase();
  const matches = useMemo(
    () =>
      q
        ? students
            .filter((s) => `${studentName(s)} ${s.last_name || ""}`.toLowerCase().includes(q) || String(s.grade || "").toLowerCase() === q)
            .slice(0, 8)
        : [],
    [students, q]
  );

  useEffect(() => {
    const onDown = (e) => wrapRef.current && !wrapRef.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);
  useEffect(() => setHi(0), [q]);

  const pick = (s) => {
    onChange(String(s.id));
    setQuery("");
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, matches.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter" && matches[hi]) { e.preventDefault(); pick(matches[hi]); }
    else if (e.key === "Escape") setOpen(false);
  };

  if (value) {
    return (
      <div className="si-student-filter">
        <div className="si-student-chip">
          <i className="fas fa-user-graduate"></i>
          <span>{selected ? studentName(selected) : `Student #${value}`}</span>
          <button type="button" aria-label="Clear student filter" onClick={() => onChange("")}><i className="fas fa-xmark"></i></button>
        </div>
      </div>
    );
  }

  return (
    <div className="si-student-filter" ref={wrapRef}>
      <i className="fas fa-user-graduate si-sf-icon"></i>
      <input
        type="text" className="si-sf-input" placeholder="Filter by student"
        role="combobox" aria-expanded={open} aria-autocomplete="list" aria-label="Filter by student"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {open && (
        <div className="si-sf-list" role="listbox">
          {!q ? (
            <div className="si-dd-empty">Type a student name to search</div>
          ) : matches.length === 0 ? (
            <div className="si-dd-empty">No student matches “{query}”</div>
          ) : (
            <>
              {matches.map((s, i) => (
                <div
                  key={s.id} role="option" aria-selected={i === hi}
                  className={`si-dd-item${i === hi ? " hi" : ""}`}
                  onMouseEnter={() => setHi(i)} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(s)}
                >
                  <span>{studentName(s) || `Student #${s.id}`}</span>
                  <span className="si-sub">{s.grade ? `Grade ${s.grade}` : ""}</span>
                </div>
              ))}
              {matches.length === 8 && <div className="si-dd-more">Showing top 8. Keep typing to narrow.</div>}
            </>
          )}
        </div>
      )}
    </div>
  );
};

/* ═════════════════════════ MAIN COMPONENT ═════════════════════════ */
const SessionInfo = () => {
  const dispatch = useDispatch();

  const sessionInfoState = useSelector((s) => s.admin?.sessionInfo || {});
  const sessionMasterState = useSelector((s) => s.admin?.sessionMaster || {});
  const studentsState = useSelector((s) => s.admin?.students || {});
  const courseInstructorState = useSelector((s) => s.admin?.courseInstructor || {});
  const branchState = useSelector((s) => s.admin?.branches || {});
  const booksList = useSelector(selectBooks) || [];

  const masters = sessionMasterState.sessionMasters || [];
  const students = useMemo(
    () => studentsState.studentList || studentsState.students || studentsState.data || [],
    [studentsState]
  );
  const instructors = courseInstructorState.courseInstructors || courseInstructorState.instructors || courseInstructorState.data || [];
  const branches = branchState.branches || branchState.branchList || [];
  const {
    sessionInfos = [], totalPages = 1, currentPage = 1, total = 0, loading = false, selectedSessionInfo = null,
  } = sessionInfoState;

  /* ── Filters ── */
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filters, setFilters] = useState({ preset: "all", month: "", from: "", to: "", studentId: "" });

  const range = useMemo(() => {
    const now = new Date();
    switch (filters.preset) {
      case "today": return { from: ymd(now), to: ymd(now) };
      case "week": {
        const day = (now.getDay() + 6) % 7; // Monday start
        const start = new Date(now); start.setDate(now.getDate() - day);
        const end = new Date(start); end.setDate(start.getDate() + 6);
        return { from: ymd(start), to: ymd(end) };
      }
      case "month": {
        const [y, m] = (filters.month || `${now.getFullYear()}-${now.getMonth() + 1}`).split("-").map(Number);
        return { from: ymd(new Date(y, m - 1, 1)), to: ymd(new Date(y, m, 0)) };
      }
      case "range": return { from: filters.from, to: filters.to };
      default: return { from: "", to: "" };
    }
  }, [filters]);

  const updateFilters = (patch) => {
    setFilters((p) => ({ ...p, ...patch }));
    dispatch(setCurrentPage(1));
  };
  const clearFilters = () => {
    setFilters({ preset: "all", month: "", from: "", to: "", studentId: "" });
    setSearchTerm("");
    dispatch(setCurrentPage(1));
  };
  const hasFilters = filters.preset !== "all" || filters.studentId || searchTerm;

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      dispatch(setCurrentPage(1));
    }, 500);
    return () => clearTimeout(t);
  }, [searchTerm, dispatch]);

  const reload = useCallback(() => {
    dispatch(fetchSessionInfos({
      page: currentPage,
      limit: LIMIT,
      search: debouncedSearch,
      date_from: range.from || undefined,
      date_to: range.to || undefined,
      student_id: filters.studentId || undefined,
    }));
  }, [dispatch, currentPage, debouncedSearch, range.from, range.to, filters.studentId]);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    dispatch(fetchSessionMasters({ page: 1, limit: 1000 }));
    dispatch(fetchStudents({ page: 1, limit: 1000, search: "", status: "active" }));
    dispatch(fetchBranches({ page: 1, limit: 1000 }));
    dispatch(fetchCourseInstructors({ page: 1, limit: 1000 }));
  }, [dispatch]);

  /* ── Modal state ── */
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(makeEmptyForm);
  const [rosterSearch, setRosterSearch] = useState("");
  const [extSearch, setExtSearch] = useState("");
  const [showExtDropdown, setShowExtDropdown] = useState(false);
  const [previewInstance, setPreviewInstance] = useState(null);
  const extRef = useRef(null);
  const snapshotRef = useRef("");

  const attendanceLocked = Number(formData.attendance_editable) === 0;
  const rosterDisabled = saving || attendanceLocked;

  /* chapters cached per book so every segment row can have its own book */
  const [chapterCache, setChapterCache] = useState({});
  const requestedBooks = useRef(new Set());

  const loadChapters = useCallback(async (bookId) => {
    if (!bookId || requestedBooks.current.has(String(bookId))) return;
    requestedBooks.current.add(String(bookId));
    try {
      const res = await dispatch(fetchBookChapters(bookId)).unwrap();
      setChapterCache((p) => ({ ...p, [bookId]: normalizeList(res) }));
    } catch {
      requestedBooks.current.delete(String(bookId));
      setChapterCache((p) => ({ ...p, [bookId]: [] }));
    }
  }, [dispatch]);

  useEffect(() => {
    const onDown = (e) => extRef.current && !extRef.current.contains(e.target) && setShowExtDropdown(false);
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  /* ── Close handling: Esc, unsaved-change guard, body scroll lock ── */
  const isDirty = () => JSON.stringify(formData) !== snapshotRef.current;
  const requestClose = useCallback(() => {
    if (saving) return;
    if (isDirty() && !window.confirm("Discard your unsaved changes?")) return;
    setShowModal(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saving, formData]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (showModal) requestClose();
      else if (previewInstance) setPreviewInstance(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showModal, previewInstance, requestClose]);

  useEffect(() => {
    document.body.style.overflow = showModal || previewInstance ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [showModal, previewInstance]);

  /* ── Lookups ── */
  const getStudent = (id) => students.find((s) => Number(s.id) === Number(id));

  const getMasterName = (masterId) => {
    const m = masters.find((x) => Number(x.id) === Number(masterId));
    if (!m) return `Master schedule #${masterId}`;
    const name = m.session_name || "";
    const one2one = m.category === "1-1 Session" || m.session_type === "1-1";
    const online = m.mode === "online" || name.toLowerCase().includes("online");
    if (one2one || online) {
      const s = m.students?.[0] ? getStudent(m.students[0]) : null;
      return s ? `${name} - ${studentName(s)}` : name;
    }
    let branch = m.branch_name || m.branch || "";
    if (!branch && m.branch_id) {
      const b = branches.find((x) => Number(x.id) === Number(m.branch_id));
      branch = b?.branch_name || b?.name || "";
    }
    return branch ? `${name} (${branch})` : name;
  };

  const getInstructorName = (id) => {
    if (!id) return "Unassigned";
    if (!instructors.length) return "Loading…";
    const m = instructors.find((i) => Number(i.instructor_id ?? i.id ?? i.userId) === Number(id));
    return m ? m.full_name || m.name || m.instructor_name : `Instructor #${id}`;
  };

  const bookName = (id) => {
    const b = booksList.find((x) => String(x.book_id || x.id) === String(id));
    return b ? b.title || b.book_name || b.name : "";
  };

  const fmtDate = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    return isNaN(dt) ? d : dt.toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" });
  };

  /* ── Details handlers ── */
  const handleMasterChange = (value) => {
    if (!value) {
      setFormData((p) => ({ ...p, session_master_id: "", instructor_id: "", attendance_roster: [], lessons: [newLesson()] }));
      dispatch(clearBookData());
      return;
    }
    const id = Number(value);
    const m = masters.find((x) => Number(x.id) === id);
    if (!m) return setFormData((p) => ({ ...p, session_master_id: id }));

    if (m.course_id) {
      dispatch(fetchBooks(m.course_id));
      dispatch(fetchCourseInstructors(m.course_id));
    } else dispatch(clearBookData());

    const roster = (m.students || []).map(Number).map((sid) => {
      const s = getStudent(sid);
      return {
        student_id: sid, attendance_status: "Absent", name: studentName(s), last_name: s?.last_name || "",
        grade: s?.grade || "", student_current_status: s?.status || "active", is_external: false, is_newly_added: false,
      };
    });
    setFormData((p) => ({
      ...p, session_master_id: id, instructor_id: m.instructor_id ? Number(m.instructor_id) : "",
      lessons: [newLesson(p.start_time, p.end_time)], attendance_roster: roster,
    }));
    toastr.info(`Loaded ${roster.length} students for this batch.`);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => {
      const next = { ...p, [name]: value };
      // A single, untouched segment follows the session window
      if ((name === "start_time" || name === "end_time") && p.lessons.length === 1) {
        const l = p.lessons[0];
        if (!l[name] || l[name] === p[name]) next.lessons = [{ ...l, [name]: value }];
      }
      return next;
    });
  };

  /* ── Segment handlers ── */
  const patchLesson = (key, patch) =>
    setFormData((p) => ({ ...p, lessons: p.lessons.map((l) => (l.key === key ? { ...l, ...patch } : l)) }));

  // New row starts exactly where the previous segment ended and reuses its length (30 min fallback)
  const addLesson = () =>
    setFormData((p) => {
      const last = p.lessons[p.lessons.length - 1];
      const start = last?.end_time || p.start_time || "";
      const len = calcDur(last?.start_time, last?.end_time) || 30;
      const end = start ? addMinutes(start, len) : "";
      return { ...p, lessons: [...p.lessons, newLesson(start, end)] };
    });

  // Changing an end time slides the following segments that were back-to-back with it.
  // Segments separated by a gap (a break) are left alone.
  const setLessonEnd = (key, value) =>
    setFormData((p) => {
      const list = p.lessons.map((l) => ({ ...l }));
      const i = list.findIndex((l) => l.key === key);
      if (i < 0) return p;
      let oldEnd = list[i].end_time;
      let prevEnd = value;
      list[i].end_time = value;
      for (let j = i + 1; j < list.length; j++) {
        const nxt = list[j];
        if (!oldEnd || !prevEnd || nxt.start_time !== oldEnd) break;
        const len = calcDur(nxt.start_time, nxt.end_time);
        oldEnd = nxt.end_time;
        nxt.start_time = prevEnd;
        nxt.end_time = len ? addMinutes(prevEnd, len) : nxt.end_time;
        prevEnd = nxt.end_time;
      }
      return { ...p, lessons: list };
    });

  const removeLesson = (key) =>
    setFormData((p) => ({
      ...p,
      lessons: p.lessons.length === 1 ? [newLesson()] : p.lessons.filter((l) => l.key !== key),
    }));

  const moveLesson = (idx, dir) =>
    setFormData((p) => {
      const next = [...p.lessons];
      const to = idx + dir;
      if (to < 0 || to >= next.length) return p;
      [next[idx], next[to]] = [next[to], next[idx]];
      return { ...p, lessons: next };
    });

  const onBookChange = (key, bookId) => {
    patchLesson(key, { book_id: bookId, chapter_id: "" });
    loadChapters(bookId);
  };

  // A typed topic wins as the title; otherwise it is built from the chosen book and chapter
  const lessonTitle = (l) => {
    if (l.title && l.title.trim()) return l.title.trim();
    if (l.book_id) {
      const bn = bookName(l.book_id);
      if (l.chapter_id) {
        const ch = (chapterCache[l.book_id] || []).find((c) => String(chapterId(c)) === String(l.chapter_id));
        const chLabel = ch ? chapterLabel({ ...ch, isSub: false }) : "";
        return chLabel ? `${bn} – ${chLabel}` : bn;
      }
      return bn;
    }
    return "Untitled lesson";
  };

  const plannedMinutes = formData.lessons.reduce((sum, l) => sum + calcDur(l.start_time, l.end_time), 0);
  const sessionMinutes = calcDur(formData.start_time, formData.end_time);
  const badSessionWindow = isBadWindow(formData.start_time, formData.end_time);

  /* ── Roster handlers ── */
  const toggleAttendance = (sid) =>
    !attendanceLocked &&
    setFormData((p) => ({
      ...p,
      attendance_roster: p.attendance_roster.map((r) =>
        Number(r.student_id) === Number(sid)
          ? { ...r, attendance_status: r.attendance_status === "Present" ? "Absent" : "Present" }
          : r
      ),
    }));

  const addExternal = (s) => {
    if (attendanceLocked) return;
    const sid = Number(s.id);
    if (formData.attendance_roster.some((r) => Number(r.student_id) === sid))
      return toastr.warning("Student is already on this roster.");
    setFormData((p) => ({
      ...p,
      attendance_roster: [...p.attendance_roster, {
        student_id: sid, attendance_status: "Present", name: studentName(s), last_name: s.last_name || "",
        grade: s.grade || "", student_current_status: s.status || "active", is_external: true, is_newly_added: true,
      }],
    }));
    setExtSearch("");
    setShowExtDropdown(false);
    toastr.success(`${studentName(s)} added as a makeup attendee.`);
  };

  const removeExternal = (sid) =>
    !attendanceLocked &&
    setFormData((p) => ({ ...p, attendance_roster: p.attendance_roster.filter((r) => Number(r.student_id) !== Number(sid)) }));

  /* ── Modal open ── */
  const openWith = (form) => {
    snapshotRef.current = JSON.stringify(form);
    setFormData(form);
    setRosterSearch(""); setExtSearch(""); setShowExtDropdown(false); setActiveTab("details");
    setShowModal(true);
  };

  const openAdd = () => {
    setIsEditMode(false);
    dispatch(clearBookData());
    openWith(makeEmptyForm());
  };

  const openEdit = (instance) => {
    setIsEditMode(true);
    dispatch(setSelectedSessionInfo(instance));
    const m = masters.find((x) => Number(x.id) === Number(instance.session_master_id));
    if (m?.course_id) {
      dispatch(fetchBooks(m.course_id));
      dispatch(fetchCourseInstructors(m.course_id));
    }

    const mapped = (m?.students || []).map(Number);
    const roster = flattenRoster(instance.attendance_roster).map((r) => {
      const sid = Number(r.student_id);
      const s = getStudent(sid);
      return {
        student_id: sid,
        attendance_status: r.attendance_status || "Absent",
        name: r.name || studentName(s),
        last_name: r.last_name || s?.last_name || "",
        grade: r.grade || s?.grade || "",
        student_current_status: r.student_current_status || s?.status || "active",
        is_external: Boolean(r.is_external) || (mapped.length > 0 && !mapped.includes(sid)),
        is_newly_added: false,
      };
    });

    const raw = instance.lessons || instance.lesson_items || [];
    let lessons = raw.map((l) => ({
      key: uid(), id: l.id || null,
      book_id: l.book_id || "", chapter_id: l.chapter_id || "",
      title: isAutoTitle(l) ? "" : l.title, notes: l.notes || "",
      start_time: hhmm(l.start_time), end_time: hhmm(l.end_time),
    }));
    if (!lessons.length) {
      // older sessions stored one topic / book on the session itself
      lessons = [{
        ...newLesson(hhmm(instance.start_time), hhmm(instance.end_time)),
        book_id: instance.book_id || "", chapter_id: instance.chapter_id || "",
        title: instance.book_id ? "" : instance.topic || "",
      }];
    }
    lessons.forEach((l) => l.book_id && loadChapters(l.book_id));

    openWith({
      session_master_id: instance.session_master_id || "",
      instructor_id: instance.instructor_id || "",
      session_date: instance.session_date || ymd(new Date()),
      start_time: hhmm(instance.start_time),
      end_time: hhmm(instance.end_time),
      notes: instance.notes || "",
      attendance_editable: instance.attendance_editable ?? 1,
      lessons,
      attendance_roster: roster,
    });
  };

  /* ── Submit ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.session_master_id || !formData.session_date) {
      setActiveTab("details");
      return toastr.warning("Select a batch and a session date.");
    }
    if (badSessionWindow) {
      setActiveTab("details");
      return toastr.warning("Session end time must be after the start time.");
    }
    for (let i = 0; i < formData.lessons.length; i++) {
      const l = formData.lessons[i];
      const n = i + 1;
      const blank = !l.book_id && !l.title.trim();
      if (blank && formData.lessons.length > 1) {
        setActiveTab("lessons");
        return toastr.warning(`Segment ${n}: choose a book or enter a topic, or remove the row.`);
      }
      if (isBadWindow(l.start_time, l.end_time)) {
        setActiveTab("lessons");
        return toastr.warning(`Segment ${n}: end time must be after start time.`);
      }
    }

    const lessons = formData.lessons
      .filter((l) => l.book_id || l.title.trim())
      .map((l, i) => ({
        id: l.id || undefined,
        session_date: formData.session_date,
        start_time: l.start_time || null,
        end_time: l.end_time || null,
        duration_minutes: calcDur(l.start_time, l.end_time),
        book_id: l.book_id || null,
        chapter_id: l.chapter_id || null,
        title: lessonTitle(l) || "Untitled lesson",
        notes: l.notes.trim() || null,
        sort_order: i,
      }));

    // Legacy columns stay populated so older screens and reports keep working
    const payload = {
      ...formData,
      lessons,
      topic: lessons[0] ? lessons[0].title + (lessons.length > 1 ? ` +${lessons.length - 1} more` : "") : "",
      book_id: lessons[0]?.book_id || "",
      chapter_id: lessons[0]?.chapter_id || "",
    };

    setSaving(true);
    try {
      if (isEditMode) {
        await dispatch(updateSessionInfo({ id: selectedSessionInfo.id, sessionInfoData: payload })).unwrap();
        toastr.success("Session updated.");
      } else {
        await dispatch(addSessionInfo(payload)).unwrap();
        toastr.success("Session created.");
      }
      setShowModal(false);
      reload();
    } catch (err) {
      toastr.error(typeof err === "string" ? err : err?.message || "Couldn't save the session. Try again.");
    } finally {
      setSaving(false);
    }
  };

  /* ── Derived ── */
  const presentCount = formData.attendance_roster.filter((r) => r.attendance_status === "Present").length;
  const rosterTotal = formData.attendance_roster.length;

  const pageNumbers = useMemo(() => {
    const start = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
    const end = Math.min(totalPages, start + 4);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [currentPage, totalPages]);

  const pageStats = useMemo(() => {
    let mins = 0, present = 0, roster = 0, locked = 0;
    sessionInfos.forEach((i) => {
      getLessons(i).forEach((l) => { mins += Number(l.duration_minutes) || calcDur(l.start_time, l.end_time); });
      const r = flattenRoster(i.attendance_roster);
      roster += r.length;
      present += r.filter((x) => x.attendance_status === "Present").length;
      if (Number(i.attendance_editable) === 0) locked += 1;
    });
    return { mins, rate: roster ? Math.round((present / roster) * 100) : null, locked };
  }, [sessionInfos]);

  const goPage = (p) => p >= 1 && p <= totalPages && dispatch(setCurrentPage(p));

  const availableExternal = students.filter((s) => {
    if (String(s.status || "active").toLowerCase() !== "active") return false;
    if (formData.attendance_roster.some((r) => Number(r.student_id) === Number(s.id))) return false;
    return studentName(s).toLowerCase().includes(extSearch.toLowerCase());
  });

  const rosterMatches = (item) =>
    (item.name || studentName(getStudent(item.student_id))).toLowerCase().includes(rosterSearch.toLowerCase());
  const isActive = (r) => String(r.student_current_status || "active").toLowerCase() === "active";
  const rosterBatch = formData.attendance_roster.filter((r) => isActive(r) && !r.is_external && rosterMatches(r));
  const rosterExternal = formData.attendance_roster.filter((r) => isActive(r) && r.is_external && rosterMatches(r));
  const rosterInactive = formData.attendance_roster.filter((r) => !isActive(r) && rosterMatches(r));

  // Drawer data
  const prevRoster = previewInstance ? flattenRoster(previewInstance.attendance_roster) : [];
  const prevPresent = prevRoster.filter((r) => r.attendance_status === "Present");
  const prevAbsent = prevRoster.filter((r) => r.attendance_status !== "Present");
  const rosterName = (r) => r.name || studentName(getStudent(r.student_id)) || `Student #${r.student_id}`;
  const prevLessons = previewInstance ? getLessons(previewInstance) : [];

  /* ═════════════════════════ RENDER ═════════════════════════ */
  return (
    <div className="si-page">
      <div className="si-wrap">
        {/* Header */}
        <header className="si-header">
          <div>
            <p className="si-eyebrow"><i className="fas fa-chalkboard-user"></i> Operations <span>/</span> Sessions</p>
            <h1 className="si-title">Session logs</h1>
            <p className="si-lede">Record what each batch covered, for how long, and who attended.</p>
          </div>
          <button type="button" className="si-btn-primary" onClick={openAdd}>
            <i className="fas fa-plus"></i> New session log
          </button>
        </header>

        {/* KPIs */}
        <section className="si-kpis" aria-label="Summary">
          {[
            ["fas fa-calendar-check", "Sessions in view", loading ? null : total],
            ["fas fa-clock", "Lesson time · this page", loading ? null : fmtDur(pageStats.mins)],
            ["fas fa-user-check", "Attendance · this page", loading ? null : pageStats.rate == null ? "—" : `${pageStats.rate}%`],
            ["fas fa-lock", "Locked · this page", loading ? null : pageStats.locked],
          ].map(([icon, label, value]) => (
            <div key={label} className="si-kpi">
              <span className="si-kpi-icon"><i className={icon}></i></span>
              <div className="si-min0">
                <span className="si-kpi-label">{label}</span>
                <span className="si-kpi-val">{value == null ? <Skeleton w={48} h={20} r={5} /> : value}</span>
              </div>
            </div>
          ))}
        </section>

        {/* Filters */}
        <section className="si-filters" aria-label="Filters">
          <div className="si-filter-row si-filter-top">
            <div className="si-search">
              <i className="fas fa-search"></i>
              <input
                type="text" placeholder="Search topic, lesson or notes"
                value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search sessions"
              />
            </div>
            <StudentFilter students={students} value={filters.studentId} onChange={(id) => updateFilters({ studentId: id })} />
          </div>

          <div className="si-filter-row">
            <div className="si-seg" role="tablist" aria-label="Date filter">
              {[["all", "All time"], ["today", "Today"], ["week", "This week"], ["month", "Month"], ["range", "Date range"]].map(([k, label]) => (
                <button
                  key={k} type="button" role="tab" aria-selected={filters.preset === k}
                  className={filters.preset === k ? "active" : ""}
                  onClick={() => updateFilters({ preset: k })}
                >
                  {label}
                </button>
              ))}
            </div>

            {filters.preset === "month" && (
              <input
                type="month" className="si-field si-inline-field" aria-label="Month"
                value={filters.month || `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`}
                onChange={(e) => updateFilters({ month: e.target.value })}
              />
            )}
            {filters.preset === "range" && (
              <div className="si-range">
                <input type="date" className="si-field si-inline-field" aria-label="From date" value={filters.from} max={filters.to || undefined} onChange={(e) => updateFilters({ from: e.target.value })} />
                <span>to</span>
                <input type="date" className="si-field si-inline-field" aria-label="To date" value={filters.to} min={filters.from || undefined} onChange={(e) => updateFilters({ to: e.target.value })} />
              </div>
            )}
            {hasFilters && <button type="button" className="si-link-btn" onClick={clearFilters}>Clear filters</button>}
            <span className="si-result-count" aria-live="polite">{loading ? "Loading…" : `${total} ${total === 1 ? "session" : "sessions"}`}</span>
          </div>
        </section>

        {/* Table */}
        <section className="si-card">
          <div className="table-responsive">
            <table className="si-table">
              <thead>
                <tr>
                  <th style={{ width: "32%" }}>Lessons covered</th>
                  <th style={{ width: "21%" }}>Batch</th>
                  <th style={{ width: "13%" }}>Instructor</th>
                  <th style={{ width: "12%" }}>Date</th>
                  <th style={{ width: "13%" }}>Attendance</th>
                  <th className="text-end" style={{ width: "9%" }}><span className="si-sr">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      <td><Skeleton w="70%" h={13} /><Skeleton w="35%" h={10} style={{ marginTop: 6 }} /></td>
                      <td><Skeleton w="60%" /></td><td><Skeleton w="50%" /></td><td><Skeleton w="70%" /></td>
                      <td><Skeleton w="55%" h={20} r={20} /></td><td><Skeleton w={60} h={30} r={8} style={{ marginLeft: "auto" }} /></td>
                    </tr>
                  ))
                ) : sessionInfos.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="si-empty">
                      <div className="si-empty-icon"><i className="fas fa-clipboard-list"></i></div>
                      <p className="si-empty-title">{hasFilters ? "No sessions match these filters" : "No session logs yet"}</p>
                      <span className="si-sub">{hasFilters ? "Widen the dates or clear the student filter." : "Create a log to start tracking lessons and attendance."}</span>
                    </td>
                  </tr>
                ) : (
                  sessionInfos.map((inst) => {
                    const lessons = getLessons(inst);
                    const roster = flattenRoster(inst.attendance_roster);
                    const present = roster.filter((r) => r.attendance_status === "Present").length;
                    const pct = roster.length ? Math.round((present / roster.length) * 100) : 0;
                    const minutes = lessons.reduce((s, l) => s + (Number(l.duration_minutes) || calcDur(l.start_time, l.end_time)), 0);
                    const first = lessons[0];
                    return (
                      <tr
                        key={inst.id} className="si-row" tabIndex={0}
                        onClick={() => setPreviewInstance(inst)}
                        onKeyDown={(e) => e.key === "Enter" && setPreviewInstance(inst)}
                      >
                        <td data-label="Lessons" className="si-td-main">
                          {first ? (
                            <>
                              <div className="si-lessons-title">
                                <span className="si-ellipsis">{first.title}</span>
                                {lessons.length > 1 && <span className="si-more">+{lessons.length - 1}</span>}
                              </div>
                              <div className="si-sub">
                                {lessons.length} {lessons.length === 1 ? "segment" : "segments"}{minutes ? ` · ${fmtDur(minutes)}` : ""}
                              </div>
                            </>
                          ) : <span className="si-sub">No lessons recorded</span>}
                        </td>
                        <td data-label="Batch"><span className="si-badge"><i className="fas fa-layer-group"></i><span className="si-ellipsis">{getMasterName(inst.session_master_id)}</span></span></td>
                        <td data-label="Instructor"><span className="si-cell">{getInstructorName(inst.instructor_id)}</span></td>
                        <td data-label="Date">
                          <div className="si-cell">{fmtDate(inst.session_date)}</div>
                          <div className="si-sub">{hhmm(inst.start_time) || "—"} – {hhmm(inst.end_time) || "—"}</div>
                        </td>
                        <td data-label="Attendance">
                          <div className="si-att">
                            <div className="si-att-top">
                              <strong>{present}/{roster.length}</strong>
                              {Number(inst.attendance_editable) === 0 && <i className="fas fa-lock si-row-lock" title="Attendance locked"></i>}
                            </div>
                            <div className="si-meter" aria-hidden="true"><span style={{ width: `${pct}%` }} /></div>
                          </div>
                        </td>
                        <td className="text-end si-td-actions" data-label="" onClick={(e) => e.stopPropagation()}>
                          <button type="button" className="si-btn-outline" onClick={() => openEdit(inst)}>Edit</button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="si-footer">
            <span className="si-sub" style={{ marginTop: 0 }}>Showing {sessionInfos.length} of {total}</span>
            <div className="si-pagination">
              <button type="button" className="si-page-btn" disabled={currentPage === 1} onClick={() => goPage(currentPage - 1)} aria-label="Previous page"><i className="fas fa-chevron-left"></i></button>
              {pageNumbers.map((p) => (
                <button key={p} type="button" className={`si-page-btn${p === currentPage ? " active" : ""}`} onClick={() => goPage(p)} aria-current={p === currentPage ? "page" : undefined}>{p}</button>
              ))}
              <button type="button" className="si-page-btn" disabled={currentPage === totalPages} onClick={() => goPage(currentPage + 1)} aria-label="Next page"><i className="fas fa-chevron-right"></i></button>
            </div>
          </div>
        </section>
      </div>

      {/* ═════════ DETAILS DRAWER (read-only) ═════════ */}
      {previewInstance && (
        <div className="si-overlay si-overlay-drawer" onClick={() => setPreviewInstance(null)}>
          <aside className="si-drawer" role="dialog" aria-modal="true" aria-label="Session details" onClick={(e) => e.stopPropagation()}>
            <div className="si-drawer-grip" aria-hidden="true" />
            <div className="si-modal-head">
              <div className="si-min0">
                <h2>Session details</h2>
                <p className="si-ellipsis">{getMasterName(previewInstance.session_master_id)}</p>
              </div>
              <button type="button" className="btn-close shadow-none" aria-label="Close" onClick={() => setPreviewInstance(null)}></button>
            </div>

            <div className="si-drawer-body">
              <div className="si-meta-grid">
                <div><span className="si-label">Date</span><strong>{fmtDate(previewInstance.session_date)}</strong></div>
                <div><span className="si-label">Time</span><strong>{hhmm(previewInstance.start_time) || "—"} – {hhmm(previewInstance.end_time) || "—"}</strong></div>
                <div><span className="si-label">Instructor</span><strong>{getInstructorName(previewInstance.instructor_id)}</strong></div>
              </div>

              <div className="si-block">
                <div className="si-block-head">
                  <h3>Attendance</h3>
                  <span className="si-block-meta">
                    {prevPresent.length} of {prevRoster.length} present
                    {Number(previewInstance.attendance_editable) === 0 && <><i className="fas fa-lock" style={{ marginLeft: 8 }}></i> Locked</>}
                  </span>
                </div>
                <div className="si-meter lg" aria-hidden="true"><span style={{ width: `${prevRoster.length ? (prevPresent.length / prevRoster.length) * 100 : 0}%` }} /></div>
                {prevRoster.length > 0 && (
                  <div className="si-chips">
                    {prevPresent.slice(0, 12).map((r) => <span key={`p${r.student_id}`} className="si-chip ok"><i className="fas fa-check"></i>{rosterName(r)}</span>)}
                    {prevPresent.length > 12 && <span className="si-chip">+{prevPresent.length - 12} more</span>}
                    {prevAbsent.slice(0, 12).map((r) => <span key={`a${r.student_id}`} className="si-chip no"><i className="fas fa-xmark"></i>{rosterName(r)}</span>)}
                    {prevAbsent.length > 12 && <span className="si-chip">+{prevAbsent.length - 12} more</span>}
                  </div>
                )}
              </div>

              {previewInstance.notes && (
                <div className="si-block">
                  <div className="si-block-head"><h3>Session notes</h3></div>
                  <p className="si-prose">{previewInstance.notes}</p>
                </div>
              )}

              <div className="si-block">
                <div className="si-block-head">
                  <h3>Lesson segments</h3>
                  <span className="si-block-meta">{prevLessons.length} {prevLessons.length === 1 ? "segment" : "segments"}</span>
                </div>
                {prevLessons.length === 0 ? (
                  <p className="si-sub">No lessons recorded for this session.</p>
                ) : (
                  <ol className="si-tl">
                    {prevLessons.map((l, idx) => {
                      const showBook = l.book_title && l.title !== l.book_title && !String(l.title).startsWith(`${l.book_title} – `);
                      return (
                        <li key={l.id || idx} style={{ "--seg": SEG_COLORS[idx % SEG_COLORS.length] }}>
                          <span className="si-tl-dot">{idx + 1}</span>
                          <div className="si-tl-body">
                            <div className="si-tl-top">
                              <span className="si-tl-title">{l.title}</span>
                              {(l.start_time || l.end_time) && (
                                <span className="si-time-chip">
                                  {hhmm(l.start_time)}–{hhmm(l.end_time)}{l.duration_minutes ? ` · ${fmtDur(Number(l.duration_minutes))}` : ""}
                                </span>
                              )}
                            </div>
                            {(showBook || l.chapter_name) && (
                              <div className="si-tl-refs">
                                {showBook && <span className="si-ref"><i className="fas fa-book"></i>{l.book_title}</span>}
                                {l.chapter_name && <span className="si-ref">{l.chapter_name}</span>}
                              </div>
                            )}
                            {l.notes && <p className="si-tl-notes">{l.notes}</p>}
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </div>
            </div>

            <div className="si-modal-foot">
              <button type="button" className="si-btn-cancel" onClick={() => setPreviewInstance(null)}>Close</button>
              <button type="button" className="si-btn-primary" onClick={() => { const inst = previewInstance; setPreviewInstance(null); openEdit(inst); }}>
                <i className="fas fa-pen"></i> Edit session log
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* ═════════ ADD / EDIT MODAL ═════════ */}
      {showModal && (
        <div className="si-overlay" onClick={(e) => e.target === e.currentTarget && requestClose()}>
          <form className="si-modal" role="dialog" aria-modal="true" aria-label={isEditMode ? "Edit session log" : "New session log"} onSubmit={handleSubmit} noValidate>
            <div className="si-modal-head">
              <div className="si-min0">
                <h2>{isEditMode ? "Edit session log" : "New session log"}</h2>
                <p>{isEditMode ? "Update lesson segments, time and attendance." : "Pick a batch and date first, then add one or more segments."}</p>
              </div>
              <button type="button" className="btn-close shadow-none" aria-label="Close" onClick={requestClose} disabled={saving}></button>
            </div>

            <div className="si-tabs" role="tablist">
              {[
                ["details", "fas fa-sliders-h", "Details", null],
                ["lessons", "fas fa-book-open", "Segments", formData.lessons.length],
                ["roster", "fas fa-user-check", "Attendance", rosterTotal ? `${presentCount}/${rosterTotal}` : null],
              ].map(([k, icon, label, badge]) => (
                <button key={k} type="button" role="tab" aria-selected={activeTab === k}
                  className={`si-tab${activeTab === k ? " active" : ""}`} onClick={() => setActiveTab(k)} disabled={saving}>
                  <i className={icon}></i>{label}
                  {k === "roster" && attendanceLocked && <i className="fas fa-lock si-tab-lock" title="View only"></i>}
                  {badge != null && <span className="si-tab-badge">{badge}</span>}
                </button>
              ))}
            </div>

            <div className="si-modal-body">
              {/* DETAILS */}
              {activeTab === "details" && (
                <div className="row g-3">
                  <div className="col-12">
                    <label className="si-label">Batch *</label>
                    <select className="si-field" value={formData.session_master_id} onChange={(e) => handleMasterChange(e.target.value)} disabled={isEditMode || saving}>
                      <option value="">Select a batch</option>
                      {masters.map((m) => <option key={m.id} value={m.id}>{getMasterName(m.id)}</option>)}
                    </select>
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="si-label">Instructor</label>
                    <select name="instructor_id" className="si-field" value={formData.instructor_id} onChange={handleChange} disabled={saving || !formData.session_master_id}>
                      <option value="">{instructors.length === 0 ? "No instructors for this course" : "Select instructor"}</option>
                      {instructors.map((c) => {
                        const cid = Number(c.instructor_id || c.id);
                        return <option key={cid} value={cid}>{c.full_name || c.name}</option>;
                      })}
                    </select>
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="si-label">Session date *</label>
                    <input type="date" name="session_date" className="si-field" value={formData.session_date} onChange={handleChange} disabled={saving} />
                  </div>

                  <div className="col-12">
                    <label className="si-label">Session window</label>
                    <div className="si-time-grid">
                      <div className="si-time-field">
                        <span>Start</span>
                        <input type="time" name="start_time" className="si-field" value={formData.start_time} onChange={handleChange} disabled={saving} aria-label="Session start time" />
                      </div>
                      <div className="si-time-field">
                        <span>End</span>
                        <input type="time" name="end_time" className={`si-field${badSessionWindow ? " invalid" : ""}`} value={formData.end_time} onChange={handleChange} disabled={saving} aria-label="Session end time" />
                      </div>
                      <div className={`si-duration-badge${badSessionWindow ? " bad" : ""}`}>
                        <span>Duration</span>
                        <strong>{badSessionWindow ? "End must be after start" : sessionMinutes ? fmtDur(sessionMinutes) : "—"}</strong>
                      </div>
                    </div>
                  </div>

                  <div className="col-12">
                    <label className="si-label">Session notes</label>
                    <textarea name="notes" rows="3" className="si-field" style={{ resize: "none" }} placeholder="Homework, general observations, anything to remember (optional)" value={formData.notes} onChange={handleChange} disabled={saving}></textarea>
                  </div>
                  <div className="col-12">
                    <div className="si-toggle-row">
                      <div>
                        <span className="si-label" style={{ marginBottom: 2 }}>Allow attendance changes</span>
                        <span className="si-sub" style={{ marginTop: 0 }}>Turn off to make the Attendance tab view-only.</span>
                      </div>
                      <div className="form-check form-switch m-0">
                        <input className="form-check-input si-switch" type="checkbox" aria-label="Allow attendance changes"
                          checked={Number(formData.attendance_editable) === 1}
                          onChange={(e) => setFormData((p) => ({ ...p, attendance_editable: e.target.checked ? 1 : 0 }))} disabled={saving} />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* SEGMENTS */}
              {activeTab === "lessons" && (
                <div>
                  <div className="si-lesson-summary">
                    <div>
                      <strong>{formData.lessons.length} {formData.lessons.length === 1 ? "segment" : "segments"}</strong>
                      <span> · {fmtDur(plannedMinutes)} planned</span>
                      {sessionMinutes > 0 && <span> of {fmtDur(sessionMinutes)} session</span>}
                    </div>
                    {sessionMinutes > 0 && plannedMinutes > sessionMinutes && (
                      <span className="si-warn"><i className="fas fa-triangle-exclamation"></i> Over session time by {fmtDur(plannedMinutes - sessionMinutes)}</span>
                    )}
                  </div>

                  {plannedMinutes > 0 && (
                    <div className="si-strip" aria-hidden="true">
                      {formData.lessons.map((l, i) => {
                        const d = calcDur(l.start_time, l.end_time);
                        return d ? <span key={l.key} style={{ flex: `${d} 0 0`, background: SEG_COLORS[i % SEG_COLORS.length] }} title={`#${i + 1} · ${fmtDur(d)}`} /> : null;
                      })}
                      {sessionMinutes > plannedMinutes && <span className="si-strip-free" style={{ flex: `${sessionMinutes - plannedMinutes} 0 0` }} />}
                    </div>
                  )}

                  {!formData.session_master_id && <div className="si-note">Select a batch in Details to load its course books.</div>}

                  <div className="si-lesson-list">
                    {formData.lessons.map((l, idx) => {
                      const chapters = buildChapterOptions(chapterCache[l.book_id] || []);
                      const dur = calcDur(l.start_time, l.end_time);
                      const badTime = isBadWindow(l.start_time, l.end_time);
                      const prev = formData.lessons[idx - 1];
                      const overlaps = Boolean(prev?.end_time && l.start_time && toMin(l.start_time) < toMin(prev.end_time));
                      return (
                        <div key={l.key} className="si-lesson" style={{ "--seg": SEG_COLORS[idx % SEG_COLORS.length] }}>
                          <div className="si-lesson-head">
                            <span className="si-lesson-no">{idx + 1}</span>
                            <span className="si-lesson-name">Segment {idx + 1}</span>
                            {overlaps && <span className="si-warn sm"><i className="fas fa-triangle-exclamation"></i> Overlaps previous</span>}
                            <div className="si-lesson-actions">
                              <button type="button" className="si-icon-btn" title="Move up" aria-label="Move segment up" onClick={() => moveLesson(idx, -1)} disabled={saving || idx === 0}><i className="fas fa-arrow-up"></i></button>
                              <button type="button" className="si-icon-btn" title="Move down" aria-label="Move segment down" onClick={() => moveLesson(idx, 1)} disabled={saving || idx === formData.lessons.length - 1}><i className="fas fa-arrow-down"></i></button>
                              <button type="button" className="si-icon-btn danger" title="Remove segment" aria-label="Remove segment" onClick={() => removeLesson(l.key)} disabled={saving}><i className="fas fa-trash-alt"></i></button>
                            </div>
                          </div>

                          <div className="si-time-grid flat">
                            <div className="si-time-field">
                              <span>Start</span>
                              <input type="time" className="si-field" value={l.start_time} onChange={(e) => patchLesson(l.key, { start_time: e.target.value })} disabled={saving} aria-label={`Segment ${idx + 1} start time`} />
                            </div>
                            <div className="si-time-field">
                              <span>End</span>
                              <input type="time" className={`si-field${badTime ? " invalid" : ""}`} value={l.end_time} onChange={(e) => setLessonEnd(l.key, e.target.value)} disabled={saving} aria-label={`Segment ${idx + 1} end time`} />
                            </div>
                            <div className={`si-duration-badge${badTime ? " bad" : ""}`}>
                              <span>Duration</span>
                              <strong>{badTime ? "End must be after start" : dur ? fmtDur(dur) : "—"}</strong>
                            </div>
                          </div>

                          <div className="row g-2 mt-0">
                            <div className="col-12 col-md-6">
                              <label className="si-label">Book <em>(optional)</em></label>
                              <select className="si-field" value={l.book_id} onChange={(e) => onBookChange(l.key, e.target.value)} disabled={saving || !formData.session_master_id || booksList.length === 0}>
                                <option value="">{booksList.length === 0 ? "No books for this course" : "No book"}</option>
                                {booksList.map((b, i) => {
                                  const id = b.book_id || b.id;
                                  return <option key={id || i} value={id}>{b.title || b.book_name || b.name || `Book #${id}`}</option>;
                                })}
                              </select>
                            </div>
                            <div className="col-12 col-md-6">
                              <label className="si-label">Chapter</label>
                              <select className="si-field" value={l.chapter_id} onChange={(e) => patchLesson(l.key, { chapter_id: e.target.value })} disabled={saving || !l.book_id}>
                                <option value="">{!l.book_id ? "Choose a book first" : chapters.length === 0 ? "No chapters found" : "Whole book / any chapter"}</option>
                                {chapters.map((c, i) => <option key={chapterId(c) || i} value={chapterId(c)}>{chapterLabel(c)}</option>)}
                              </select>
                            </div>
                            <div className="col-12">
                              <label className="si-label">Custom topic <em>(optional if a book is selected)</em></label>
                              <input type="text" className="si-field" placeholder="e.g. Students played games, mate-in-2 puzzles, opening review" value={l.title} onChange={(e) => patchLesson(l.key, { title: e.target.value })} disabled={saving} />
                            </div>
                            <div className="col-12">
                              <label className="si-label">Notes</label>
                              <input type="text" className="si-field" placeholder="Homework or observations for this segment (optional)" value={l.notes} onChange={(e) => patchLesson(l.key, { notes: e.target.value })} disabled={saving} />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <button type="button" className="si-add-lesson" onClick={addLesson} disabled={saving}>
                    <i className="fas fa-plus"></i> Add another segment
                  </button>
                </div>
              )}

              {/* ATTENDANCE */}
              {activeTab === "roster" && (
                <div>
                  {attendanceLocked ? (
                    <div className="si-lock-banner" role="status">
                      <i className="fas fa-lock"></i>
                      <div>
                        <strong>Attendance is locked</strong>
                        <span>This session is view-only. Turn on “Allow attendance changes” in Details to edit.</span>
                      </div>
                    </div>
                  ) : (
                    <div className="si-note">Tap a student to switch between Present and Absent. Inactive students are locked to protect past records.</div>
                  )}

                  {!attendanceLocked && (
                    <div className="si-external-box" ref={extRef}>
                      <label className="si-label" style={{ color: "var(--brand-ink)" }}><i className="fas fa-user-plus" style={{ marginRight: 6 }}></i>Add a makeup student from another batch</label>
                      <div className="si-dd-wrap">
                        <i className="fas fa-search si-dd-icon"></i>
                        <input type="text" className="si-field" style={{ paddingLeft: 34 }}
                          placeholder={formData.session_master_id ? "Search active students" : "Select a batch first"}
                          value={extSearch}
                          onChange={(e) => { setExtSearch(e.target.value); setShowExtDropdown(true); }}
                          onFocus={() => formData.session_master_id && setShowExtDropdown(true)}
                          disabled={saving || !formData.session_master_id} />
                        {showExtDropdown && formData.session_master_id && (
                          <div className="si-dd-list">
                            {!extSearch.trim() ? (
                              <div className="si-dd-empty">Type a name to search active students</div>
                            ) : availableExternal.length === 0 ? (
                              <div className="si-dd-empty">No active students match “{extSearch}”</div>
                            ) : availableExternal.slice(0, 8).map((s) => (
                              <div key={s.id} className="si-dd-item" onClick={() => addExternal(s)}>
                                <span>{studentName(s) || `Student #${s.id}`}</span>
                                <span className="si-sub" style={{ marginTop: 0 }}>{s.grade ? `Grade ${s.grade}` : ""}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <input type="text" className="si-field" style={{ margin: "12px 0" }} placeholder="Filter attendees" value={rosterSearch} onChange={(e) => setRosterSearch(e.target.value)} disabled={saving} />

                  {rosterTotal === 0 ? (
                    <div className="si-roster-empty"><i className="fas fa-users-slash"></i>No students yet. Pick a batch in Details to load its roster.</div>
                  ) : (
                    <div className="si-roster-list">
                      {rosterBatch.map((r) => <RosterRow key={`b-${r.student_id}`} item={r} sObj={getStudent(r.student_id)} onToggle={toggleAttendance} onRemove={removeExternal} disabled={rosterDisabled} />)}
                      {rosterExternal.length > 0 && <div className="si-group-label blue">Makeup attendees</div>}
                      {rosterExternal.map((r) => <RosterRow key={`e-${r.student_id}`} item={r} sObj={getStudent(r.student_id)} onToggle={toggleAttendance} onRemove={removeExternal} disabled={rosterDisabled} />)}
                      {rosterInactive.length > 0 && <div className="si-group-label">Inactive students (history)</div>}
                      {rosterInactive.map((r) => <RosterRow key={`i-${r.student_id}`} item={r} sObj={getStudent(r.student_id)} onToggle={toggleAttendance} onRemove={removeExternal} disabled={rosterDisabled} />)}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="si-modal-foot">
              <span className="si-foot-meta">{formData.lessons.length} {formData.lessons.length === 1 ? "segment" : "segments"} · {fmtDur(plannedMinutes)} · {presentCount}/{rosterTotal} present</span>
              <div className="si-foot-actions">
                <button type="button" className="si-btn-cancel" onClick={requestClose} disabled={saving}>Cancel</button>
                <button type="submit" className="si-btn-primary si-btn-submit" disabled={saving}>
                  {saving ? <><Spinner size={15} />Saving…</> : isEditMode ? "Save changes" : "Create session log"}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        @keyframes si-spin { to { transform: rotate(360deg); } }
        @keyframes si-shimmer { from { background-position: -600px 0; } to { background-position: 600px 0; } }
        @keyframes si-pop { from { opacity: 0; transform: translateY(10px) scale(.985); } to { opacity: 1; transform: none; } }
        @keyframes si-slide-in { from { transform: translateX(28px); opacity: 0; } to { transform: none; opacity: 1; } }
        @keyframes si-sheet-in { from { transform: translateY(32px); opacity: 0; } to { transform: none; opacity: 1; } }
        @keyframes si-fade { from { opacity: 0; } to { opacity: 1; } }
        @media (prefers-reduced-motion: reduce) { .si-page * { animation: none !important; transition: none !important; } }

        /* ───── Design tokens ───── */
        .si-page {
          --ink:#0b1220; --text2:#475467; --text3:#8a94a6; --line:#e4e8ef; --line2:#cfd6e2;
          --canvas:#f4f6fa; --surface:#fff; --sunken:#f8f9fc;
          --brand:#1d4ed8; --brand-ink:#1e3a8a; --brand-soft:#eef2ff; --brand-line:#c7d2fe;
          --ok:#067647; --ok-soft:#ecfdf3; --warn:#b54708; --warn-soft:#fffaeb; --bad:#b42318; --bad-soft:#fef3f2;
          --r-sm:8px; --r-md:12px; --r-lg:16px;
          --shadow-1:0 1px 2px rgba(11,18,32,.05); --shadow-2:0 1px 2px rgba(11,18,32,.04), 0 14px 32px -20px rgba(11,18,32,.22);
          background: var(--canvas); min-height: 100vh; color: var(--ink); font-size: 14px; line-height: 1.45;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; -webkit-font-smoothing: antialiased;
        }
        .si-page::before { content: ""; display: block; height: 3px; background: linear-gradient(90deg, #0b1220, #1d4ed8 62%, #7c9cf5); }
        .si-page *, .si-page *::before, .si-page *::after { box-sizing: border-box; }
        .si-page button:focus-visible, .si-page input:focus-visible, .si-page select:focus-visible, .si-page textarea:focus-visible, .si-row:focus-visible, .si-roster-row:focus-visible { outline: 2px solid var(--brand); outline-offset: 2px; }
        .si-wrap { max-width: 1240px; margin: 0 auto; padding: 28px 24px 48px; }
        .si-min0 { min-width: 0; }
        .si-ellipsis { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; }
        .si-sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); }
        .si-sub { display: block; margin-top: 2px; font-size: 12px; color: var(--text3); }
        .si-skel { background: linear-gradient(90deg,#eef0f5 25%,#e3e6ee 50%,#eef0f5 75%); background-size: 600px 100%; animation: si-shimmer 1.4s infinite linear; }

        /* ───── Header ───── */
        .si-header { display: flex; justify-content: space-between; align-items: flex-end; gap: 16px; flex-wrap: wrap; margin-bottom: 20px; }
        .si-eyebrow { margin: 0 0 6px; font-size: 12px; font-weight: 500; color: var(--text3); display: flex; align-items: center; gap: 8px; }
        .si-eyebrow i { color: var(--brand); } .si-eyebrow span { opacity: .5; }
        .si-title { margin: 0; font-size: 1.75rem; font-weight: 700; letter-spacing: -.03em; }
        .si-lede { margin: 4px 0 0; color: var(--text2); font-size: 13.5px; }

        /* ───── Buttons ───── */
        .si-btn-primary { background: linear-gradient(180deg, #2457e6, #1d4ed8); color: #fff; border: 0; border-radius: 10px; padding: 10px 18px; font-size: 13px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; gap: 8px; white-space: nowrap; box-shadow: 0 1px 2px rgba(11,18,32,.2), inset 0 1px 0 rgba(255,255,255,.18); transition: filter .15s, transform .05s; }
        .si-btn-primary:hover:not(:disabled) { filter: brightness(1.06); }
        .si-btn-primary:active:not(:disabled) { transform: translateY(1px); }
        .si-btn-primary:disabled { opacity: .65; cursor: not-allowed; }
        .si-btn-submit { min-width: 172px; }
        .si-btn-outline, .si-btn-cancel { border: 1px solid var(--line2); background: var(--surface); border-radius: 9px; padding: 7px 14px; font-size: 12.5px; font-weight: 600; color: var(--ink); cursor: pointer; transition: background .12s, border-color .12s; }
        .si-btn-outline:hover, .si-btn-cancel:hover:not(:disabled) { background: var(--sunken); border-color: #b8c1d1; }
        .si-btn-cancel { padding: 10px 18px; color: var(--text2); }
        .si-link-btn { background: none; border: 0; color: var(--brand); font-size: 12.5px; font-weight: 600; cursor: pointer; padding: 6px; }
        .si-icon-btn { width: 32px; height: 32px; border: 1px solid var(--line); background: var(--surface); border-radius: 8px; color: var(--text2); font-size: 12px; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .si-icon-btn:hover:not(:disabled) { background: var(--sunken); color: var(--ink); }
        .si-icon-btn.danger { color: var(--bad); border-color: #fecdca; } .si-icon-btn.danger:hover:not(:disabled) { background: var(--bad-soft); color: var(--bad); }
        .si-icon-btn:disabled { opacity: .35; cursor: not-allowed; }

        /* ───── KPIs ───── */
        .si-kpis { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; margin-bottom: 14px; }
        .si-kpi { display: flex; align-items: center; gap: 14px; background: var(--surface); border: 1px solid var(--line); border-radius: var(--r-lg); padding: 16px; box-shadow: var(--shadow-1); }
        .si-kpi-icon { width: 40px; height: 40px; border-radius: 11px; background: var(--brand-soft); color: var(--brand); display: inline-flex; align-items: center; justify-content: center; font-size: 15px; flex-shrink: 0; }
        .si-kpi-label { display: block; font-size: 12px; color: var(--text2); font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .si-kpi-val { display: flex; align-items: center; min-height: 28px; font-size: 1.4rem; font-weight: 700; letter-spacing: -.02em; font-variant-numeric: tabular-nums; }

        /* ───── Filters ───── */
        .si-filters { background: var(--surface); border: 1px solid var(--line); border-radius: var(--r-lg); padding: 12px 14px; margin-bottom: 14px; display: flex; flex-direction: column; gap: 10px; box-shadow: var(--shadow-1); }
        @media (min-width: 992px) { .si-filters { position: sticky; top: 0; z-index: 20; box-shadow: 0 8px 20px -14px rgba(11,18,32,.25); } }
        .si-filter-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .si-filter-top { flex-wrap: nowrap; }
        .si-search { position: relative; flex: 1; min-width: 0; }
        .si-search i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--text3); font-size: 12px; }
        .si-search input, .si-sf-input { width: 100%; height: 40px; border: 1px solid var(--line2); border-radius: 10px; padding: 0 12px 0 34px; font-size: 13px; background: var(--surface); font-family: inherit; color: var(--ink); }
        .si-search input:focus, .si-sf-input:focus { border-color: var(--brand); box-shadow: 0 0 0 3px rgba(29,78,216,.14); outline: none; }
        .si-inline-field { width: auto; height: 36px; padding: 0 10px; }
        .si-range { display: flex; align-items: center; gap: 8px; color: var(--text2); font-size: 12.5px; }
        .si-result-count { margin-left: auto; font-size: 12.5px; font-weight: 500; color: var(--text2); font-variant-numeric: tabular-nums; }
        .si-seg { display: inline-flex; background: var(--canvas); border: 1px solid var(--line); border-radius: 10px; padding: 3px; gap: 2px; max-width: 100%; overflow-x: auto; scrollbar-width: none; }
        .si-seg::-webkit-scrollbar { display: none; }
        .si-seg button { border: 0; background: transparent; border-radius: 7px; padding: 6px 12px; font-size: 12.5px; color: var(--text2); cursor: pointer; font-weight: 500; white-space: nowrap; }
        .si-seg button.active { background: var(--surface); color: var(--brand); font-weight: 600; box-shadow: 0 1px 2px rgba(11,18,32,.1); }

        .si-student-filter { position: relative; width: 290px; flex-shrink: 0; }
        .si-sf-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--text3); font-size: 12px; pointer-events: none; z-index: 1; }
        .si-sf-list { position: absolute; top: calc(100% + 4px); left: 0; right: 0; z-index: 50; background: var(--surface); border: 1px solid var(--line2); border-radius: 12px; box-shadow: 0 16px 32px -10px rgba(11,18,32,.25); padding: 4px; }
        .si-student-chip { display: flex; align-items: center; gap: 8px; height: 40px; padding: 0 6px 0 12px; background: var(--brand-soft); border: 1px solid var(--brand-line); border-radius: 10px; color: var(--brand-ink); font-size: 13px; font-weight: 600; }
        .si-student-chip i { font-size: 12px; }
        .si-student-chip span { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .si-student-chip button { border: 0; background: transparent; width: 28px; height: 28px; border-radius: 7px; color: var(--brand-ink); cursor: pointer; }
        .si-student-chip button:hover { background: #dbe3ff; }

        .si-dd-wrap { position: relative; }
        .si-dd-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--text3); font-size: 12px; z-index: 1; }
        .si-dd-list { position: absolute; top: calc(100% + 4px); left: 0; right: 0; max-height: 220px; overflow-y: auto; background: var(--surface); border: 1px solid var(--line2); border-radius: 12px; box-shadow: 0 16px 32px -10px rgba(11,18,32,.25); z-index: 100; padding: 4px; }
        .si-dd-item { padding: 9px 12px; border-radius: 8px; cursor: pointer; display: flex; justify-content: space-between; align-items: center; gap: 10px; font-size: 13px; font-weight: 500; }
        .si-dd-item:hover, .si-dd-item.hi { background: var(--brand-soft); }
        .si-dd-empty { padding: 14px 12px; text-align: center; font-size: 12.5px; color: var(--text3); }
        .si-dd-more { padding: 8px 12px 6px; font-size: 11.5px; color: var(--text3); border-top: 1px solid var(--line); margin-top: 4px; }

        /* ───── Table ───── */
        .si-card { background: var(--surface); border: 1px solid var(--line); border-radius: var(--r-lg); overflow: hidden; box-shadow: var(--shadow-2); }
        .si-table { width: 100%; border-collapse: collapse; }
        .si-table thead th { background: var(--sunken); font-size: 11.5px; font-weight: 600; color: var(--text2); text-transform: uppercase; letter-spacing: .05em; padding: 12px 18px; border-bottom: 1px solid var(--line); white-space: nowrap; }
        .si-table tbody tr { border-bottom: 1px solid var(--line); }
        .si-table tbody tr:last-child { border-bottom: 0; }
        .si-row { cursor: pointer; transition: background .12s; }
        .si-row:hover { background: #f7f9ff; }
        .si-table td { padding: 14px 18px; vertical-align: middle; }
        .si-lessons-title { display: flex; align-items: center; gap: 8px; font-size: 13.5px; font-weight: 600; max-width: 340px; }
        .si-more { flex-shrink: 0; font-size: 11px; font-weight: 700; color: var(--brand); background: var(--brand-soft); border-radius: 20px; padding: 1px 8px; }
        .si-cell { font-size: 13px; font-weight: 500; color: var(--ink); }
        .si-badge { display: inline-flex; align-items: center; gap: 7px; background: var(--canvas); border: 1px solid var(--line); padding: 4px 10px; border-radius: 8px; font-size: 12.5px; font-weight: 500; max-width: 100%; }
        .si-badge i { color: var(--text3); font-size: 11px; flex-shrink: 0; }
        .si-att { min-width: 92px; }
        .si-att-top { display: flex; align-items: center; gap: 8px; font-size: 13px; font-variant-numeric: tabular-nums; }
        .si-row-lock { color: var(--text3); font-size: 11px; }
        .si-meter { height: 5px; background: var(--line); border-radius: 99px; overflow: hidden; margin-top: 6px; }
        .si-meter > span { display: block; height: 100%; background: linear-gradient(90deg, #1d4ed8, #4f7cf0); border-radius: 99px; transition: width .3s; }
        .si-meter.lg { height: 8px; margin: 8px 0 12px; }
        .si-empty { text-align: center; padding: 56px 16px; }
        .si-empty-icon { width: 56px; height: 56px; background: var(--canvas); border-radius: 16px; display: inline-flex; align-items: center; justify-content: center; font-size: 1.35rem; color: var(--text3); margin-bottom: 12px; }
        .si-empty-title { font-size: 14.5px; font-weight: 600; margin: 0 0 4px; }
        .si-footer { display: flex; justify-content: space-between; align-items: center; padding: 12px 18px; border-top: 1px solid var(--line); gap: 10px; flex-wrap: wrap; background: var(--sunken); }
        .si-pagination { display: flex; gap: 4px; }
        .si-page-btn { min-width: 32px; height: 32px; padding: 0 8px; border: 1px solid var(--line); background: var(--surface); border-radius: 8px; font-size: 12.5px; font-weight: 600; color: var(--ink); cursor: pointer; display: inline-flex; align-items: center; justify-content: center; }
        .si-page-btn:hover:not(:disabled):not(.active) { background: var(--canvas); }
        .si-page-btn.active { background: var(--brand); border-color: var(--brand); color: #fff; }
        .si-page-btn:disabled { opacity: .35; cursor: not-allowed; }

        /* ───── Overlays, drawer, modal ───── */
        .si-overlay { position: fixed; inset: 0; background: rgba(11,18,32,.45); backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px); display: flex; align-items: center; justify-content: center; z-index: 9999; padding: 16px; animation: si-fade .18s ease; }
        .si-overlay-drawer { justify-content: flex-end; padding: 0; }
        .si-drawer { width: 100%; max-width: 520px; height: 100%; background: var(--surface); display: flex; flex-direction: column; box-shadow: -24px 0 60px -20px rgba(11,18,32,.35); animation: si-slide-in .24s cubic-bezier(.2,.8,.2,1); }
        .si-drawer-grip { display: none; }
        .si-drawer-body { flex: 1; overflow-y: auto; padding: 20px 24px; display: flex; flex-direction: column; gap: 20px; }
        .si-modal { width: 100%; max-width: 880px; height: min(700px, 92vh); background: var(--surface); border-radius: 20px; border: 1px solid var(--line); box-shadow: 0 40px 90px -24px rgba(11,18,32,.45); display: flex; flex-direction: column; overflow: hidden; animation: si-pop .22s ease; }
        .si-modal-head { padding: 20px 24px 14px; display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; background: linear-gradient(180deg, #fbfcfe, #fff); border-bottom: 1px solid var(--line); }
        .si-modal-head h2 { margin: 0; font-size: 17px; font-weight: 700; letter-spacing: -.01em; }
        .si-modal-head p { margin: 3px 0 0; font-size: 12.5px; color: var(--text2); }
        .si-tabs { display: flex; gap: 2px; padding: 0 18px; border-bottom: 1px solid var(--line); overflow-x: auto; scrollbar-width: none; background: var(--surface); }
        .si-tabs::-webkit-scrollbar { display: none; }
        .si-tab { display: inline-flex; align-items: center; gap: 8px; border: 0; background: transparent; padding: 12px 14px; font-size: 13px; font-weight: 500; color: var(--text2); cursor: pointer; border-bottom: 2px solid transparent; margin-bottom: -1px; white-space: nowrap; }
        .si-tab:hover:not(.active) { color: var(--ink); }
        .si-tab.active { color: var(--brand); font-weight: 600; border-bottom-color: var(--brand); }
        .si-tab-badge { background: var(--canvas); color: var(--text2); font-size: 11px; font-weight: 700; border-radius: 20px; padding: 1px 8px; }
        .si-tab.active .si-tab-badge { background: var(--brand); color: #fff; }
        .si-tab-lock { font-size: 11px; color: var(--text3); }
        .si-modal-body { flex: 1; overflow-y: auto; padding: 22px 24px; background: var(--surface); overscroll-behavior: contain; }
        .si-modal-foot { padding: 12px 24px; border-top: 1px solid var(--line); background: var(--sunken); display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; }
        .si-foot-meta { font-size: 12.5px; color: var(--text2); font-variant-numeric: tabular-nums; }
        .si-foot-actions { display: flex; gap: 10px; }

        /* ───── Drawer content ───── */
        .si-meta-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; background: var(--sunken); border: 1px solid var(--line); border-radius: var(--r-md); padding: 14px; }
        .si-meta-grid strong { display: block; font-size: 13px; font-weight: 600; overflow-wrap: anywhere; }
        .si-block-head { display: flex; justify-content: space-between; align-items: baseline; gap: 10px; }
        .si-block-head h3 { margin: 0; font-size: 12px; font-weight: 700; color: var(--text2); text-transform: uppercase; letter-spacing: .06em; }
        .si-block-meta { font-size: 12px; color: var(--text2); font-weight: 500; }
        .si-prose { margin: 8px 0 0; font-size: 13px; color: var(--text2); background: var(--sunken); border: 1px solid var(--line); border-radius: var(--r-md); padding: 12px 14px; white-space: pre-wrap; }
        .si-chips { display: flex; flex-wrap: wrap; gap: 6px; }
        .si-chip { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 500; border-radius: 20px; padding: 3px 10px; background: var(--canvas); color: var(--text2); }
        .si-chip i { font-size: 10px; }
        .si-chip.ok { background: var(--ok-soft); color: var(--ok); } .si-chip.no { background: var(--bad-soft); color: var(--bad); }
        .si-tl { list-style: none; margin: 12px 0 0; padding: 0; display: flex; flex-direction: column; }
        .si-tl li { position: relative; display: flex; gap: 14px; padding-bottom: 16px; }
        .si-tl li:not(:last-child)::before { content: ""; position: absolute; left: 13px; top: 28px; bottom: 0; width: 2px; background: var(--line); }
        .si-tl-dot { width: 28px; height: 28px; border-radius: 50%; background: var(--seg); color: #fff; font-size: 12px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .si-tl-body { flex: 1; min-width: 0; padding-top: 3px; }
        .si-tl-top { display: flex; justify-content: space-between; align-items: flex-start; gap: 10px; flex-wrap: wrap; }
        .si-tl-title { font-size: 13.5px; font-weight: 600; overflow-wrap: anywhere; }
        .si-time-chip { font-size: 11.5px; font-weight: 600; color: var(--text2); background: var(--canvas); border-radius: 6px; padding: 2px 8px; font-variant-numeric: tabular-nums; white-space: nowrap; }
        .si-tl-refs { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px; }
        .si-ref { display: inline-flex; align-items: center; gap: 6px; font-size: 11.5px; font-weight: 500; color: var(--brand-ink); background: var(--brand-soft); border-radius: 6px; padding: 2px 8px; }
        .si-tl-notes { margin: 6px 0 0; font-size: 12.5px; color: var(--text2); }

        /* ───── Form ───── */
        .si-label { display: block; font-size: 12.5px; font-weight: 600; color: var(--text2); margin-bottom: 6px; }
        .si-label em { font-style: normal; font-weight: 400; color: var(--text3); }
        .si-field { width: 100%; min-height: 40px; border: 1px solid var(--line2); border-radius: 10px; padding: 9px 12px; font-size: 13px; color: var(--ink); background: var(--surface); font-family: inherit; transition: border-color .15s, box-shadow .15s; }
        .si-field:focus { border-color: var(--brand); box-shadow: 0 0 0 3px rgba(29,78,216,.14); outline: none; }
        .si-field:disabled { background: var(--canvas); color: var(--text3); cursor: not-allowed; }
        .si-field.invalid { border-color: var(--bad); box-shadow: 0 0 0 3px rgba(180,35,24,.1); }
        .si-note { background: var(--sunken); border: 1px solid var(--line); border-radius: 10px; padding: 11px 14px; font-size: 12.5px; color: var(--text2); line-height: 1.55; margin-bottom: 14px; }
        .si-toggle-row { display: flex; justify-content: space-between; align-items: center; gap: 12px; background: var(--sunken); border: 1px solid var(--line); border-radius: 12px; padding: 14px 16px; }
        .si-switch { width: 2.6em; height: 1.3em; cursor: pointer; margin: 0; flex-shrink: 0; }

        .si-time-grid { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(150px, auto); gap: 10px; align-items: end; background: var(--sunken); border: 1px solid var(--line); border-radius: var(--r-md); padding: 12px; }
        .si-time-grid.flat { background: transparent; border: 0; padding: 0; margin-bottom: 12px; }
        .si-time-field { display: flex; flex-direction: column; gap: 5px; min-width: 0; }
        .si-time-field > span, .si-duration-badge > span { font-size: 11.5px; font-weight: 600; color: var(--text3); text-transform: uppercase; letter-spacing: .05em; }
        .si-duration-badge { display: flex; flex-direction: column; justify-content: center; gap: 2px; min-height: 40px; background: var(--surface); border: 1px dashed var(--line2); border-radius: 10px; padding: 4px 12px; white-space: nowrap; }
        .si-duration-badge strong { font-size: 13px; font-weight: 600; }
        .si-duration-badge.bad { background: var(--bad-soft); border-color: #fecdca; } .si-duration-badge.bad strong { color: var(--bad); }

        .si-lesson-summary { display: flex; justify-content: space-between; align-items: center; gap: 10px; flex-wrap: wrap; font-size: 13px; color: var(--text2); margin-bottom: 10px; }
        .si-lesson-summary strong { color: var(--ink); }
        .si-warn { color: var(--warn); font-size: 12.5px; font-weight: 600; } .si-warn.sm { font-size: 11.5px; background: var(--warn-soft); border-radius: 20px; padding: 2px 8px; }
        .si-strip { display: flex; gap: 3px; height: 8px; margin-bottom: 16px; border-radius: 99px; overflow: hidden; background: var(--canvas); }
        .si-strip > span { display: block; min-width: 4px; border-radius: 99px; }
        .si-strip-free { background: repeating-linear-gradient(135deg, #e4e8ef 0 4px, #f1f3f8 4px 8px); }
        .si-lesson-list { display: flex; flex-direction: column; gap: 14px; }
        .si-lesson { border: 1px solid var(--line2); border-left: 4px solid var(--seg, var(--brand)); border-radius: var(--r-md); padding: 14px 16px 16px; background: var(--surface); box-shadow: var(--shadow-1); transition: border-color .15s, box-shadow .15s; }
        .si-lesson:focus-within { box-shadow: 0 0 0 3px rgba(29,78,216,.1), var(--shadow-1); }
        .si-lesson-head { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; }
        .si-lesson-no { width: 26px; height: 26px; border-radius: 50%; background: var(--seg, var(--brand)); color: #fff; font-size: 12px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .si-lesson-name { font-size: 13px; font-weight: 600; }
        .si-lesson-actions { margin-left: auto; display: flex; gap: 6px; }
        .si-add-lesson { width: 100%; margin-top: 14px; border: 1.5px dashed #aab8f5; background: var(--brand-soft); color: var(--brand); border-radius: var(--r-md); padding: 14px; font-size: 13px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; gap: 8px; transition: background .12s; }
        .si-add-lesson:hover:not(:disabled) { background: #e0e7ff; }
        .si-add-lesson:disabled { opacity: .6; cursor: not-allowed; }

        /* ───── Attendance ───── */
        .si-external-box { background: var(--brand-soft); border: 1px solid var(--brand-line); border-radius: var(--r-md); padding: 14px 16px; }
        .si-roster-list { display: flex; flex-direction: column; gap: 8px; }
        .si-roster-row { display: flex; justify-content: space-between; align-items: center; gap: 10px; min-height: 56px; padding: 10px 14px; border: 1px solid var(--line); border-radius: 12px; background: var(--surface); cursor: pointer; user-select: none; transition: background .12s, border-color .12s; }
        .si-roster-row:hover:not(.locked):not(.readonly) { background: var(--sunken); border-color: var(--line2); }
        .si-roster-row.present { border-color: var(--brand-line); background: #f5f7ff; }
        .si-roster-row.external { border-left: 3px solid var(--brand); }
        .si-roster-row.locked { background: var(--sunken); opacity: .85; cursor: default; }
        .si-roster-row.readonly { cursor: default; }
        .si-roster-left, .si-roster-right { display: flex; align-items: center; gap: 12px; min-width: 0; }
        .si-roster-right { gap: 8px; flex-shrink: 0; }
        .si-avatar { width: 36px; height: 36px; border-radius: 50%; background: var(--canvas); color: var(--text2); display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 13px; flex-shrink: 0; }
        .si-avatar.on { background: var(--brand); color: #fff; }
        .si-roster-name { display: flex; align-items: center; gap: 6px; font-weight: 600; font-size: 13px; min-width: 0; }
        .si-tag { flex-shrink: 0; font-size: 10.5px; font-weight: 700; padding: 1px 7px; border-radius: 20px; }
        .si-tag.blue { color: var(--brand-ink); background: #dbeafe; } .si-tag.amber { color: var(--warn); background: #fef0c7; }
        .si-status { display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; }
        .si-status.absent { background: var(--surface); border: 1px solid var(--line2); color: var(--text2); }
        .si-status.present { background: var(--brand); border: 1px solid var(--brand); color: #fff; }
        .si-group-label { font-size: 11.5px; font-weight: 700; color: var(--text3); text-transform: uppercase; letter-spacing: .06em; margin: 14px 0 2px 2px; }
        .si-group-label.blue { color: var(--brand-ink); }
        .si-roster-empty { padding: 40px 16px; text-align: center; color: var(--text2); font-size: 13px; }
        .si-roster-empty i { font-size: 1.5rem; color: var(--line2); margin-bottom: 10px; display: block; }
        .si-lock-banner { display: flex; gap: 12px; align-items: flex-start; background: var(--warn-soft); border: 1px solid #fedf89; color: #7a4a05; border-radius: 12px; padding: 14px 16px; margin-bottom: 14px; }
        .si-lock-banner i { margin-top: 3px; } .si-lock-banner strong { display: block; font-size: 13px; } .si-lock-banner span { font-size: 12.5px; }

        /* ───── Tablet ───── */
        @media (max-width: 991px) {
          .si-kpis { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .si-table thead th, .si-table td { padding-left: 14px; padding-right: 14px; }
        }

        /* ───── Mobile ───── */
        @media (max-width: 768px) {
          .si-wrap { padding: 18px 14px 40px; }
          .si-title { font-size: 1.45rem; }
          .si-header { align-items: stretch; }
          .si-header .si-btn-primary { width: 100%; }
          .si-filter-top { flex-wrap: wrap; }
          .si-student-filter { width: 100%; }
          .si-result-count { margin-left: 0; width: 100%; }
          .si-field, .si-sf-input, .si-search input { font-size: 16px; } /* stops iOS zoom on focus */

          .si-table thead { display: none; }
          .si-table, .si-table tbody { display: block; }
          .si-table tbody tr { display: block; margin: 10px; padding: 12px 14px; border: 1px solid var(--line); border-radius: var(--r-md); box-shadow: var(--shadow-1); }
          .si-table tbody tr:last-child { border-bottom: 1px solid var(--line); }
          .si-table td { display: flex; justify-content: space-between; align-items: center; gap: 14px; padding: 7px 0; border: 0; width: 100% !important; text-align: right !important; }
          .si-table td::before { content: attr(data-label); font-size: 11.5px; font-weight: 600; color: var(--text3); text-transform: uppercase; letter-spacing: .04em; flex-shrink: 0; }
          .si-table td.si-td-main { display: block; text-align: left !important; padding-top: 0; padding-bottom: 10px; margin-bottom: 4px; border-bottom: 1px solid var(--line); }
          .si-table td.si-td-main::before { display: none; }
          .si-lessons-title { max-width: none; }
          .si-table td.si-td-actions { justify-content: stretch; padding-bottom: 0; }
          .si-table td.si-td-actions::before { display: none; }
          .si-table td.si-td-actions .si-btn-outline { width: 100%; padding: 10px; }
          .si-table td .si-badge { max-width: 70%; }
          .si-att { min-width: 120px; text-align: left; }
          .si-empty { display: block !important; width: 100%; }
          .si-empty::before { display: none; }

          .si-overlay { padding: 0; align-items: flex-end; }
          .si-modal { max-width: 100vw; width: 100vw; height: 100dvh; max-height: 100dvh; border-radius: 0; border: 0; animation: si-sheet-in .22s ease; }
          .si-modal-head { padding: 16px 16px 12px; }
          .si-modal-body { padding: 16px; }
          .si-modal-foot { padding: 12px 16px max(12px, env(safe-area-inset-bottom)); background: var(--surface); }
          .si-foot-meta { width: 100%; }
          .si-foot-actions { width: 100%; }
          .si-foot-actions > * { flex: 1; min-width: 0; }
          .si-lesson { padding: 12px 12px 14px; }
          .si-lesson-head { flex-wrap: wrap; }

          /* drawer becomes a bottom sheet */
          .si-overlay-drawer { align-items: flex-end; justify-content: stretch; }
          .si-drawer { max-width: none; height: auto; max-height: 92dvh; border-radius: 20px 20px 0 0; animation: si-sheet-in .24s cubic-bezier(.2,.8,.2,1); }
          .si-drawer-grip { display: block; width: 40px; height: 4px; background: var(--line2); border-radius: 99px; margin: 8px auto 0; }
          .si-drawer-body { padding: 16px; }
          .si-meta-grid { grid-template-columns: 1fr 1fr; }
          .si-drawer .si-modal-foot { background: var(--surface); }
        }

        /* ───── Small phones ───── */
        @media (max-width: 560px) {
          .si-kpis { gap: 10px; }
          .si-kpi { padding: 12px; gap: 10px; }
          .si-kpi-icon { width: 34px; height: 34px; font-size: 13px; }
          .si-kpi-val { font-size: 1.15rem; }
          .si-time-grid { grid-template-columns: 1fr 1fr; }
          .si-time-grid .si-duration-badge { grid-column: 1 / -1; flex-direction: row; align-items: center; justify-content: space-between; }
          .si-range { width: 100%; flex-wrap: wrap; }
          .si-range .si-inline-field { flex: 1; min-width: 130px; }
          .si-status { padding: 6px 10px; }
          .si-pagination .si-page-btn:not(.active):not([aria-label]) { display: none; }
        }
        @media (max-width: 380px) { .si-kpis { grid-template-columns: 1fr; } .si-meta-grid { grid-template-columns: 1fr; } }
      `}</style>
    </div>
  );
};

export default SessionInfo;
